import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../../services/api';
import { PerfilPermissao, ModuloSistema, AcaoSistema, PermissaoPerfil, CreatePerfilPermissaoData, UpdatePerfilPermissaoData } from '../../../../types/admin';
import { MatrizPermissoes } from './MatrizPermissoes';

interface ModalEditorPerfilProps {
  perfil: PerfilPermissao | null;
  isCreating: boolean;
  modulos: ModuloSistema[];
  acoes: AcaoSistema[];
  onClose: () => void;
  onRefresh: () => void;
}

interface FormData {
  nome: string;
  codigo: string;
  descricao: string;
  cor: string;
  ativo: boolean;
}

export const ModalEditorPerfil: React.FC<ModalEditorPerfilProps> = ({
  perfil,
  isCreating,
  modulos,
  acoes,
  onClose,
  onRefresh
}) => {
  const [activeTab, setActiveTab] = useState<'informacoes' | 'permissoes'>('informacoes');
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    codigo: '',
    descricao: '',
    cor: '#6B7280',
    ativo: true
  });
  const [permissoesExistentes, setPermissoesExistentes] = useState<PermissaoPerfil[]>([]);
  const [permissoesAtuais, setPermissoesAtuais] = useState<{ id_modulo: number; id_acao: number; permitido: boolean }[]>([]);

  // Usar useCallback para evitar re-renders infinitos
  const handlePermissoesChange = useCallback((novasPermissoes: { id_modulo: number; id_acao: number; permitido: boolean }[]) => {
    setPermissoesAtuais(novasPermissoes);
  }, []);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Cores pré-definidas
  const coresDisponiveis = [
    '#DC2626', // Red
    '#059669', // Green  
    '#2563EB', // Blue
    '#7C3AED', // Purple
    '#EA580C', // Orange
    '#0891B2', // Cyan
    '#65A30D', // Lime
    '#C2410C', // Orange-600
    '#9333EA', // Violet
    '#6B7280'  // Gray (padrão)
  ];

  // Inicializar formulário quando o perfil muda
  useEffect(() => {
    if (perfil && !isCreating) {
      setFormData({
        nome: perfil.nome,
        codigo: perfil.codigo,
        descricao: perfil.descricao || '',
        cor: perfil.cor || '#6B7280',
        ativo: perfil.ativo
      });
      fetchPermissoesPerfil(perfil.id);
    } else if (isCreating) {
      setFormData({
        nome: '',
        codigo: '',
        descricao: '',
        cor: '#6B7280',
        ativo: true
      });
      setPermissoesExistentes([]);
    }
  }, [perfil, isCreating]);

  // Buscar permissões existentes do perfil
  const fetchPermissoesPerfil = async (perfilId: number) => {
    try {
      const response = await apiService.getPermissoesPerfil({ id_perfil: perfilId });
      setPermissoesExistentes(response.data);
    } catch (error) {
      console.error('Erro ao buscar permissões do perfil:', error);
    }
  };

  // Validar formulário
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    } else if (formData.nome.length < 2) {
      newErrors.nome = 'Nome deve ter pelo menos 2 caracteres';
    }

    if (!formData.codigo.trim()) {
      newErrors.codigo = 'Código é obrigatório';
    } else if (!/^[A-Z_][A-Z0-9_]*$/.test(formData.codigo)) {
      newErrors.codigo = 'Código deve conter apenas letras maiúsculas, números e underscore';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salvar perfil
  const handleSave = async () => {
    if (!validateForm()) {
      setActiveTab('informacoes');
      return;
    }

    setLoading(true);
    try {
      let perfilId: number;

      if (isCreating) {
        // Criar novo perfil
        const createData: CreatePerfilPermissaoData = {
          id_conta: 2, // ID da conta do sistema (fixo por enquanto)
          nome: formData.nome,
          codigo: formData.codigo,
          descricao: formData.descricao || undefined,
          cor: formData.cor
        };

        const response = await apiService.createPerfilPermissao(createData);
        perfilId = response.data.id;
      } else {
        // Atualizar perfil existente
        if (!perfil) return;
        
        const updateData: UpdatePerfilPermissaoData = {
          nome: formData.nome,
          descricao: formData.descricao || undefined,
          cor: formData.cor
        };

        await apiService.updatePerfilPermissao(perfil.id, updateData);
        perfilId = perfil.id;
      }

      // Salvar permissões
      if (permissoesAtuais.length > 0) {
        console.log('=== DEBUG ModalEditorPerfil ===');
        console.log('ID do perfil:', perfilId);
        console.log('Total de permissões a enviar:', permissoesAtuais.length);
        console.log('Primeiras 5 permissões:', permissoesAtuais.slice(0, 5));
        console.log('Dados completos enviados para API:', {
          id_perfil: perfilId,
          permissoes: permissoesAtuais
        });
        
        // Verificar estrutura dos dados
        const permissoesInvalidas = permissoesAtuais.filter(p => 
          typeof p.id_modulo !== 'number' || 
          typeof p.id_acao !== 'number' || 
          typeof p.permitido !== 'boolean'
        );
        
        if (permissoesInvalidas.length > 0) {
          console.error('Permissões com estrutura inválida:', permissoesInvalidas);
        }
        
        await apiService.updateBulkPermissoesPerfil({
          id_perfil: perfilId,
          permissoes: permissoesAtuais
        });
      }

      onRefresh();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar perfil:', error);
      // Mostrar erro para o usuário
    } finally {
      setLoading(false);
    }
  };

  // Gerar código automaticamente baseado no nome
  const generateCodigo = (nome: string) => {
    return nome
      .toUpperCase()
      .replace(/[ÁÀÂÃÄ]/g, 'A')
      .replace(/[ÉÈÊË]/g, 'E')
      .replace(/[ÍÌÎÏ]/g, 'I')
      .replace(/[ÓÒÔÕÖ]/g, 'O')
      .replace(/[ÚÙÛÜ]/g, 'U')
      .replace(/[Ç]/g, 'C')
      .replace(/[^A-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  // Atualizar campo do formulário
  const updateFormData = (field: keyof FormData, value: any) => {
    // Gerar código automaticamente se estiver criando e o campo nome foi alterado
    let updatedData: Partial<FormData> = { [field]: value };
    
    if (field === 'nome' && isCreating) {
      const novoCodigo = generateCodigo(value);
      updatedData.codigo = novoCodigo;
    }

    setFormData(prev => ({
      ...prev,
      ...updatedData
    }));

    // Limpar erro do campo
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <div 
      className="modal fade show" 
      id="modal_editor_perfil" 
      style={{ display: 'block' }}
      aria-modal="true"
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header bg-light">
            <div className="d-flex align-items-center">
              <div 
                className="avatar avatar-md rounded-circle d-flex align-items-center justify-content-center text-white fw-bold me-3"
                style={{ 
                  backgroundColor: formData.cor,
                  width: '48px',
                  height: '48px'
                }}
              >
                {formData.nome.charAt(0).toUpperCase() || '?'}
              </div>
              <div>
                <h5 className="modal-title mb-0">
                  {isCreating ? 'Criar Novo Perfil' : `Editando: ${perfil?.nome}`}
                </h5>
                <small className="text-muted">
                  {isCreating ? 'Configure as informações e permissões do novo perfil' : 'Altere as informações e permissões do perfil'}
                </small>
              </div>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body p-0">
            {/* Nav Tabs */}
            <div className="border-bottom">
              <nav className="nav nav-tabs nav-line-tabs nav-line-tabs-2x">
                <button
                  className={`nav-link ${activeTab === 'informacoes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('informacoes')}
                >
                  <i className="ti ti-info-circle me-2"></i>
                  Informações Básicas
                </button>
                <button
                  className={`nav-link ${activeTab === 'permissoes' ? 'active' : ''}`}
                  onClick={() => setActiveTab('permissoes')}
                >
                  <i className="ti ti-shield-check me-2"></i>
                  Matriz de Permissões
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-4">
              {/* Aba Informações */}
              {activeTab === 'informacoes' && (
                <div className="tab-pane active">
                  <div className="row">
                    {/* Coluna da Esquerda */}
                    <div className="col-md-8">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Nome do Perfil <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${errors.nome ? 'is-invalid' : ''}`}
                              value={formData.nome}
                              onChange={(e) => updateFormData('nome', e.target.value)}
                              placeholder="Ex: Administrador, Vendedor..."
                            />
                            {errors.nome && <div className="invalid-feedback">{errors.nome}</div>}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Código Único <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${errors.codigo ? 'is-invalid' : ''}`}
                              value={formData.codigo}
                              onChange={(e) => updateFormData('codigo', e.target.value.toUpperCase())}
                              placeholder="Ex: ADMINISTRADOR, VENDEDOR..."
                              disabled={!isCreating && perfil?.is_padrao}
                            />
                            {errors.codigo && <div className="invalid-feedback">{errors.codigo}</div>}
                            {!isCreating && perfil?.is_padrao && (
                              <small className="text-muted">Código não pode ser alterado em perfis padrão</small>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Descrição</label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={formData.descricao}
                          onChange={(e) => updateFormData('descricao', e.target.value)}
                          placeholder="Descreva o propósito e responsabilidades deste perfil..."
                        />
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Status</label>
                            <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={formData.ativo}
                                onChange={(e) => updateFormData('ativo', e.target.checked)}
                                disabled={!isCreating && perfil?.is_padrao}
                              />
                              <label className="form-check-label">
                                {formData.ativo ? (
                                  <span className="text-success">✅ Ativo</span>
                                ) : (
                                  <span className="text-danger">❌ Inativo</span>
                                )}
                              </label>
                            </div>
                            {!isCreating && perfil?.is_padrao && (
                              <small className="text-muted">Status não pode ser alterado em perfis padrão</small>
                            )}
                            {!isCreating && !perfil?.is_padrao && (
                              <small className="text-muted">Funcionalidade de status será implementada em breve</small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Cor de Identificação</label>
                            <div className="d-flex flex-wrap gap-2">
                              {coresDisponiveis.map(cor => (
                                <button
                                  key={cor}
                                  type="button"
                                  className={`btn p-0 border-2 ${
                                    formData.cor === cor ? 'border-dark' : 'border-light'
                                  }`}
                                  style={{
                                    backgroundColor: cor,
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%'
                                  }}
                                  onClick={() => updateFormData('cor', cor)}
                                  title={cor}
                                />
                              ))}
                            </div>
                            <small className="text-muted">Cor usada para identificar visualmente o perfil</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Coluna da Direita - Preview */}
                    <div className="col-md-4">
                      <div className="card bg-light">
                        <div className="card-body">
                          <h6 className="card-title">Preview do Perfil</h6>
                          <div className="d-flex align-items-center mb-3">
                            <div 
                              className="avatar avatar-md rounded-circle d-flex align-items-center justify-content-center text-white fw-bold me-3"
                              style={{ 
                                backgroundColor: formData.cor,
                                width: '40px',
                                height: '40px'
                              }}
                            >
                              {formData.nome.charAt(0).toUpperCase() || '?'}
                            </div>
                            <div>
                              <h6 className="mb-0">{formData.nome || 'Nome do Perfil'}</h6>
                              <small className="text-muted">{formData.codigo || 'CODIGO_PERFIL'}</small>
                            </div>
                          </div>
                          
                          <div className="mb-2">
                            <small className="text-muted d-block">Status:</small>
                            {formData.ativo ? (
                              <span className="badge bg-success">Ativo</span>
                            ) : (
                              <span className="badge bg-danger">Inativo</span>
                            )}
                          </div>

                          {formData.descricao && (
                            <div>
                              <small className="text-muted d-block">Descrição:</small>
                              <p className="small mb-0">{formData.descricao}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Aba Permissões */}
              {activeTab === 'permissoes' && (
                <div className="tab-pane active">
                  <div className="mb-3">
                    <h6>Configuração de Permissões</h6>
                    <p className="text-muted">
                      Configure quais ações este perfil pode executar em cada módulo do sistema.
                    </p>
                  </div>

                  <MatrizPermissoes
                    modulos={modulos}
                    acoes={acoes}
                    permissoesExistentes={permissoesExistentes}
                    onChange={handlePermissoesChange}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            
            {activeTab === 'informacoes' ? (
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={() => {
                  if (validateForm()) {
                    setActiveTab('permissoes');
                  }
                }}
              >
                Próximo: Permissões
                <i className="ti ti-arrow-right ms-1"></i>
              </button>
            ) : (
              <>
                <button 
                  type="button" 
                  className="btn btn-outline-primary"
                  onClick={() => setActiveTab('informacoes')}
                  disabled={loading}
                >
                  <i className="ti ti-arrow-left me-1"></i>
                  Voltar
                </button>
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-device-floppy me-1"></i>
                      {isCreating ? 'Criar Perfil' : 'Salvar Alterações'}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};